import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Link, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Navbar from './Components/NavBar';
import SponsorPopupScheduler from './Components/SponsorPopupScheduler';
import CalendarLoadingShell from './Components/CalendarLoadingShell';
import SearchBar from './Components/SearchBar';
import QuickFilters from './Components/QuickFilters';
import ErrorBoundary from './Components/ErrorBoundary';
import EmailVerificationBanner from './Components/EmailVerificationBanner';
import SavedFilterSync from './Components/SavedFilterSync';
import MobileAppNavigation from './Components/MobileAppNavigation';
import SEO from './Components/SEO';
import Footer from './Components/Footer';
import {
  ACTIVE_PROMOTED_EVENT,
  ACTIVE_PROMOTED_EVENT_BANNER_EXPIRES_AT,
  collectorsParadiseGoldCoastImage,
} from './data/promotedEvents';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import AnalyticsRuntime from './analytics/AnalyticsRuntime';
import GoogleAnalyticsRuntime from './analytics/GoogleAnalyticsRuntime';
import AnalyticsConsentBanner from './Components/AnalyticsConsent';
import { API_ENDPOINTS, AU_STATE_OPTIONS, BREAKPOINTS } from './constants';
import { startVersionChecking, performHardReload, UpdatePrompt } from './utils/versionCheck';
import styled from 'styled-components';
// The footer pill (Footer.tsx SponsorLink) has a fixed #ffffff background in
// both light and dark app themes, so these are the on-light brand variants
// (cream #fffcf4 fill swapped for the brand blue #1B6FE8) - same treatment as
// NavBar/PromoPopup.
import cardReviveMark from './Assets/logos/cardrevive-mark-onlight.svg';
import cardReviveWordmark from './Assets/logos/cardrevive-wordmark-onlight.svg';
import appLogoLight from './Assets/logos/tcg-calendar-logo-light-nav-128.webp';
import appLogoDark from './Assets/logos/tcg-calendar-logo-dark-nav-128.webp';
import cardhubAustralia from './Assets/logos/cardhub-logo-Black-96.webp';
import StateFilter from './Components/StateFilter';
import {
  buildCompetitionMeta,
  buildHomeMeta,
  CARDHUB_INTRO_URL,
  CARDREVIVE_INTRO_URL,
  hasSupporterIntro,
  HOME_COMPACT_SUMMARY,
  HOME_DISCOVERY_DETAIL,
  HOME_DISCOVERY_INTRO,
  HOME_GAME_LINKS,
} from './data/seoRoutes';

// Lazy-loaded routes (not needed on initial page load)
const Settings = lazy(() => import('./pages/Settings'));
// The current calendar is a purpose-built agenda/date-rail component and no
// longer renders FullCalendar. Keep it in one named async chunk so public
// calendar routes can preload exactly the code they use.
const Calendar = lazy(() => import(/* webpackChunkName: "calendar-view" */ './Components/Calendar'));
const HomePromoBanner = lazy(() => import('./Components/HomePromoBanner'));
const ReleaseNotes = lazy(() => import('./pages/ReleaseNotes'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Contact = lazy(() => import('./pages/Contact'));
const Partners = lazy(() => import('./pages/Partners'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const SavedEvents = lazy(() => import('./pages/SavedEvents'));
const SubmitEvent = lazy(() => import('./pages/SubmitEvent'));
const MyEvents = lazy(() => import('./pages/MyEvents'));
const AdminSubmissions = lazy(() => import('./pages/AdminSubmissions'));
const SetsPage = lazy(() => import('./pages/SetsPage'));
const SetDetail = lazy(() => import('./pages/SetDetail'));
const EventModal = lazy(() => import('./Components/EventModal'));
const PromotionRequestModal = lazy(() => import('./Components/PromotionRequestModal'));
const UpdateNotification = lazy(() => import('./Components/UpdateNotification'));
// Requested only once SponsorPopupScheduler (mounted below, inside the
// Router) decides the visitor has been visibly browsing for
// SPONSOR_POPUP_DELAY_MS and it is safe to interrupt: see
// scripts/verify-performance.js's runtime:sponsor-popup-* guards, which
// assert this stays a dynamic import and never lands in the entry chunk.
const PromoPopup = lazy(() => import(/* webpackChunkName: "sponsor-popup" */ './Components/PromoPopup'));

const DeferredCalendar: React.FC<{ searchTerm: string; endpoint: string }> = (props) => (
  <Suspense fallback={<CalendarLoadingShell />}>
    <Calendar {...props} />
  </Suspense>
);

// Every lazy route below gets its own error boundary, so a failed dynamic
// import (most commonly a deleted hashed chunk left over from a previous
// deploy, see ErrorBoundary.tsx for the chunk-load detection and one-shot
// reload) only takes out that route's content, not NavBar, Footer and the
// mobile navigation with it. The home page and the six competition pages
// already wrap themselves in their own <ErrorBoundary>; this covers every
// other lazy route, with the outer <ErrorBoundary> around the Router
// remaining the last resort for anything outside a route (see App below).
const RouteErrorBoundary: React.FC<{ children: React.ReactNode; fallback: React.ReactNode }> = ({
  children,
  fallback,
}) => (
  <ErrorBoundary>
    <Suspense fallback={fallback}>{children}</Suspense>
  </ErrorBoundary>
);

// The event agenda needs enough vertical room for its date rail and grouped
// cards. Compact laptops switch the page to natural document scrolling before
// the agenda becomes cramped.
const CALENDAR_STACK_BREAKPOINT = 1180;
const SHORT_CALENDAR_VIEWPORT_HEIGHT = 900;
const COMPACT_CALENDAR_HEIGHT = 680;

const AppContainer = styled.div`
  width: 100%;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile browsers */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: var(--background);

  @media (max-width: ${CALENDAR_STACK_BREAKPOINT}px) and (min-width: 769px),
    (max-height: ${SHORT_CALENDAR_VIEWPORT_HEIGHT}px) and (min-width: 769px) {
    min-height: 100vh;
    min-height: 100dvh;
    height: auto;
    overflow: visible;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    min-height: 100vh;
    min-height: 100dvh;
    height: auto;
    overflow: visible;
    padding-bottom: calc(62px + env(safe-area-inset-bottom)); /* Room for the fixed mobile dock below the footer. */

    @supports not (height: 100dvh) {
      min-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    }
  }
`;

const SkipLink = styled.a`
  position: fixed;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 2000;
  transform: translateY(-150%);
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  padding: 0.65rem 0.9rem;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s ease;

  &:focus {
    transform: translateY(0);
    color: #fff;
  }
`;

const BodyArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${CALENDAR_STACK_BREAKPOINT}px) and (min-width: 769px),
    (max-height: ${SHORT_CALENDAR_VIEWPORT_HEIGHT}px) and (min-width: 769px) {
    flex: 1 0 auto;
    overflow: visible;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    overflow: visible;
    flex: none;
    height: auto;
  }
`;

const FullWidthBanners = styled.div`
  flex-shrink: 0;
`;

const BodyLeft = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0.5rem;
    overflow: hidden;
  }

  @media (max-width: ${CALENDAR_STACK_BREAKPOINT}px) and (min-width: 769px),
    (max-height: ${SHORT_CALENDAR_VIEWPORT_HEIGHT}px) and (min-width: 769px) {
    flex: 1 0 auto;
    overflow: visible;

    main {
      flex: 1 0 auto;
      overflow: visible;
    }
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    overflow: visible;

    main {
      padding: 0.25rem;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      flex: 1;
      min-height: 0;
    }
  }
`;

const MainContent = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: minmax(0, 1fr);
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  align-items: stretch;

  @media (max-width: ${CALENDAR_STACK_BREAKPOINT}px) and (min-width: 769px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: ${COMPACT_CALENDAR_HEIGHT}px;
    grid-auto-rows: auto;
    align-items: start;
    overflow: visible;
    flex: none;
    min-height: auto;
    padding-bottom: 1rem;
  }

  @media (min-width: ${CALENDAR_STACK_BREAKPOINT + 1}px) and (max-height: ${SHORT_CALENDAR_VIEWPORT_HEIGHT}px) {
    grid-auto-rows: ${COMPACT_CALENDAR_HEIGHT}px;
    overflow: visible;
    flex: none;
    min-height: ${COMPACT_CALENDAR_HEIGHT}px;
    padding-bottom: 1rem;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    gap: 0.25rem;
    overflow: visible;
    flex: none;
    min-height: auto;
    padding-bottom: 1rem;
  }
`;

const CalendarSection = styled.div`
  min-height: 0;
  height: 100%;
  max-height: 100%;
  flex: 1 1 auto;
  overflow: hidden; /* Prevent calendar from overflowing */
  border-radius: 8px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${CALENDAR_STACK_BREAKPOINT}px) and (min-width: 769px),
    (max-height: ${SHORT_CALENDAR_VIEWPORT_HEIGHT}px) and (min-width: 769px) {
    height: ${COMPACT_CALENDAR_HEIGHT}px;
    min-height: ${COMPACT_CALENDAR_HEIGHT}px;
    max-height: none;
    overflow: visible;
  }
  
  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    overflow: visible; /* Allow natural flow on mobile */
    min-height: auto;
    height: auto;
    display: block;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  flex-shrink: 0;

  > * {
    margin-bottom: 0 !important;
  }

  > *:last-child {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    flex-direction: column;

    > * {
      width: 100%;
    }
  }
`;

const CalendarToolsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  min-width: 0;

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    width: 100%;

    > * {
      width: 100%;
    }
  }
`;

const CalendarFeedLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0.3rem 0.58rem;
  border-radius: 7px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  box-shadow: 0 2px 8px var(--shadow);

  &:hover,
  &:focus-visible {
    background: color-mix(in srgb, var(--primary) 10%, var(--surface));
    color: var(--text);
    text-decoration: none;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    width: 100%;
    min-height: 44px;
  }
`;

const PageIntro = styled.header`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.35rem;
  color: var(--text);

  h1 {
    margin: 0;
    font-size: clamp(1.15rem, 2vw, 1.55rem);
    line-height: 1.2;
    font-weight: 750;
    flex: 0 1 auto;
  }

  p {
    margin: 0;
    max-width: none;
    color: var(--text-secondary);
    font-size: 0.92rem;
    line-height: 1.35;
    flex: 1 1 28rem;

    a {
      color: var(--primary);
      font-weight: 600;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.35rem 0.25rem;
  }
`;

const HomeCompactIntro = styled.header`
  display: grid;
  grid-template-columns: minmax(14rem, auto) minmax(0, 1fr);
  align-items: center;
  gap: 0.3rem 0.85rem;
  margin: 0 0 0.4rem;
  padding: 0.42rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-secondary);

  h1 {
    margin: 0;
    color: var(--text);
    font-size: clamp(1.15rem, 2vw, 1.55rem);
    line-height: 1.2;
    font-weight: 750;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    display: none;
  }
`;

const HomeIntroContent = styled.div`
  min-width: 0;
`;

const HomeIntroSummary = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.2rem 0.45rem;
  font-size: 0.88rem;
  line-height: 1.35;

  a {
    color: var(--primary);
    font-weight: 650;
    white-space: nowrap;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const HomeIntroToggle = styled.button<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--primary);
  font: inherit;
  font-weight: 650;
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }

  span {
    display: inline-block;
    transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
    transition: transform 160ms ease;
  }
`;

const HomeIntroPanel = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? 'grid' : 'none')};
  gap: 0.15rem;
  margin-top: 0.45rem;
  padding-top: 0.45rem;
  border-top: 1px solid var(--border);

  p {
    margin: 0;
    font-size: 0.82rem;
    line-height: 1.4;
  }

  a {
    color: var(--primary);
    font-weight: 650;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}px) {
    margin-top: 0.55rem;
  }
`;

// Honest, human-visible cross-promotion to CardRevive (Bill's Australian
// trading card restoration and grading business) and The Card Hub Australia
// (the co-owner's business), appended as one sentence to the intro copy on
// the homepage and each competition hub's "all states" view. Both partners
// get equal visible credit; CardRevive keeps its existing UTM-tagged URL and
// descriptive anchor text, while The Card Hub's link matches its existing
// footer destination exactly, with no UTM parameters added. rel="sponsored"
// declares the commercial relationship to search engines; noopener/noreferrer
// is the standard safety pairing for target="_blank" links.
const CardReviveIntroNote: React.FC = () => (
  <>
    TCG Calendar is supported by{' '}
    <a href={CARDREVIVE_INTRO_URL} target="_blank" rel="sponsored noopener noreferrer">
      CardRevive
    </a>
    , Australia's trading card restoration and grading service, and{' '}
    <a href={CARDHUB_INTRO_URL} target="_blank" rel="sponsored noopener noreferrer">
      The Card Hub Australia
    </a>
    .
  </>
);

const RouteIntro: React.FC<{ title: string; description: string; extra?: React.ReactNode }> = ({ title, description, extra }) => (
  <PageIntro>
    <h1>{title}</h1>
    <p>{description}{extra ? <> {extra}</> : null}</p>
  </PageIntro>
);

const HomePageIntro: React.FC<{ title: string; description: string }> = ({ title, description }) => {
  const [expanded, setExpanded] = useState(false);
  const panelId = 'home-calendar-guide';

  return (
    <HomeCompactIntro>
      <h1>{title}</h1>
      <HomeIntroContent>
        <HomeIntroSummary>
          <span>{HOME_COMPACT_SUMMARY}</span>
          <HomeIntroToggle
            type="button"
            $expanded={expanded}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded((current) => !current)}
          >
            Browse game calendars <span aria-hidden="true">▾</span>
          </HomeIntroToggle>
          <span aria-hidden="true">·</span>
          <Link to="/submit-event">Submit a show</Link>
        </HomeIntroSummary>
        <HomeIntroPanel
          id={panelId}
          $expanded={expanded}
          hidden={!expanded}
          role="region"
          aria-label="About shows and organised play"
        >
          <p>{description} <CardReviveIntroNote /></p>
          <p>{HOME_DISCOVERY_INTRO}</p>
          <p>
            Browse{' '}
            {HOME_GAME_LINKS.map((game, index) => (
              <React.Fragment key={game.path}>
                {index > 0 ? (index === HOME_GAME_LINKS.length - 1 ? ', or ' : ', ') : null}
                <Link to={game.path}>{game.label}</Link>
              </React.Fragment>
            ))}
            . {HOME_DISCOVERY_DETAIL} You can also <Link to="/submit-event">submit a show for review</Link>.
          </p>
        </HomeIntroPanel>
      </HomeIntroContent>
    </HomeCompactIntro>
  );
};

// Per-route SEO copy (titles, descriptions, breadcrumbs) now lives in the
// shared source of truth at src/data/seoRoutes.js so the prerender script and
// react-helmet render identical head content. See buildCompetitionMeta etc.

const VALID_STATE_CODES = new Set<string>(AU_STATE_OPTIONS.map((option) => option.code));

const getValidStateCode = (value: string | null) => {
  const stateCode = (value || 'ALL').toUpperCase();
  return VALID_STATE_CODES.has(stateCode) ? stateCode : 'ALL';
};

type CalendarFeedSource = 'shows' | 'pokemon' | 'onepiece' | 'magic' | 'riftbound' | 'lorcana' | 'fleshandblood';

const CALENDAR_FEED_NAMES: Record<CalendarFeedSource, string> = {
  shows: 'Australian trading card shows',
  pokemon: 'Pokemon TCG competitions',
  onepiece: 'One Piece Card Game tournaments',
  magic: 'Magic: The Gathering tournaments',
  riftbound: 'Riftbound events',
  lorcana: 'Disney Lorcana events',
  fleshandblood: 'Flesh and Blood events',
};

const CalendarTools: React.FC<{ source: CalendarFeedSource; selectedState?: string }> = ({
  source,
  selectedState = 'ALL',
}) => {
  const params = new URLSearchParams({ source });
  const stateCode = getValidStateCode(selectedState);
  const stateName = stateCode === 'ALL'
    ? undefined
    : AU_STATE_OPTIONS.find((option) => option.code === stateCode)?.label;

  if (stateCode !== 'ALL') params.set('state', stateCode);

  const feedName = CALENDAR_FEED_NAMES[source];
  const accessibleName = `Subscribe to ${feedName}${stateName ? ` in ${stateName}` : ''}`;

  return (
    <CalendarToolsRow>
      <QuickFilters />
      <CalendarFeedLink
        href={`webcal://www.tcgcalendar.com.au/api/calendar.ics?${params.toString()}`}
        aria-label={accessibleName}
        title={accessibleName}
      >
        Subscribe to calendar
      </CalendarFeedLink>
    </CalendarToolsRow>
  );
};

const statePath = (basePath: string, stateCode: string) => (
  stateCode === 'ALL' ? basePath : `${basePath}/${stateCode.toLowerCase()}`
);

const useUrlStateFilter = (basePath: string, routeStateCode?: string) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Path segments are the canonical state selector. Legacy ?state= links are
  // redirected after the first render so the prerendered hub body and React's
  // initial H1, introduction and canonical stay identical.
  const selectedState = getValidStateCode(routeStateCode || null);

  useEffect(() => {
    if (routeStateCode && getValidStateCode(routeStateCode) === 'ALL' && routeStateCode.toUpperCase() !== 'ALL') {
      navigate(basePath, { replace: true });
      return;
    }

    if (searchParams.has('state')) {
      const nextParams = new URLSearchParams(searchParams);
      const legacyState = getValidStateCode(nextParams.get('state'));
      nextParams.delete('state');

      navigate(
        {
          pathname: routeStateCode ? statePath(basePath, selectedState) : statePath(basePath, legacyState),
          search: nextParams.toString() ? `?${nextParams.toString()}` : '',
        },
        { replace: true }
      );
    }
  }, [basePath, navigate, routeStateCode, searchParams, selectedState]);

  const setSelectedState = useCallback((stateCode: string) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextState = getValidStateCode(stateCode);
    nextParams.delete('state');

    navigate(
      {
        pathname: statePath(basePath, nextState),
        search: nextParams.toString() ? `?${nextParams.toString()}` : '',
      },
      { replace: true }
    );
  }, [basePath, navigate, searchParams]);

  return [selectedState, setSelectedState] as const;
};

interface CompetitionsPageProps {
  searchTerm: string;
  onSearch: (term: string) => void;
}

const CompetitionsPage: React.FC<CompetitionsPageProps> = ({ searchTerm, onSearch }) => {
  const { stateCode } = useParams();
  const [selectedState, setSelectedState] = useUrlStateFilter('/pokemon-competitions', stateCode);
  const seo = buildCompetitionMeta('pokemon', selectedState);

  const competitionsEndpoint = selectedState === 'ALL'
    ? API_ENDPOINTS.COMPETITIONS
    : `${API_ENDPOINTS.COMPETITIONS}?state=${selectedState}`;

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <RouteIntro
          title={seo.heading}
          description={seo.description}
          extra={hasSupporterIntro(seo.path) ? <CardReviveIntroNote /> : undefined}
        />
        <FilterRow>
          <StateFilter
            selectedState={selectedState}
            onChange={setSelectedState}
          />
          <SearchBar
            onSearch={onSearch}
            inlineControls={<CalendarTools source="pokemon" selectedState={selectedState} />}
          />
        </FilterRow>
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={competitionsEndpoint} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

const OnePieceCompetitionsPage: React.FC<CompetitionsPageProps> = ({ searchTerm, onSearch }) => {
  const { stateCode } = useParams();
  const [selectedState, setSelectedState] = useUrlStateFilter('/onepiece-competitions', stateCode);
  const seo = buildCompetitionMeta('onepiece', selectedState);

  const endpoint = selectedState === 'ALL'
    ? API_ENDPOINTS.ONEPIECE_COMPETITIONS
    : `${API_ENDPOINTS.ONEPIECE_COMPETITIONS}?state=${selectedState}`;

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <RouteIntro
          title={seo.heading}
          description={seo.description}
          extra={hasSupporterIntro(seo.path) ? <CardReviveIntroNote /> : undefined}
        />
        <FilterRow>
          <StateFilter
            selectedState={selectedState}
            onChange={setSelectedState}
          />
          <SearchBar
            onSearch={onSearch}
            inlineControls={<CalendarTools source="onepiece" selectedState={selectedState} />}
          />
        </FilterRow>
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={endpoint} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

const MagicCompetitionsPage: React.FC<CompetitionsPageProps> = ({ searchTerm, onSearch }) => {
  const { stateCode } = useParams();
  const [selectedState, setSelectedState] = useUrlStateFilter('/magic-competitions', stateCode);
  const seo = buildCompetitionMeta('magic', selectedState);

  const endpoint = selectedState === 'ALL'
    ? API_ENDPOINTS.MAGIC_COMPETITIONS
    : `${API_ENDPOINTS.MAGIC_COMPETITIONS}?state=${selectedState}`;

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <RouteIntro
          title={seo.heading}
          description={seo.description}
          extra={hasSupporterIntro(seo.path) ? <CardReviveIntroNote /> : undefined}
        />
        <FilterRow>
          <StateFilter
            selectedState={selectedState}
            onChange={setSelectedState}
          />
          <SearchBar
            onSearch={onSearch}
            inlineControls={<CalendarTools source="magic" selectedState={selectedState} />}
          />
        </FilterRow>
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={endpoint} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

const RiftboundCompetitionsPage: React.FC<CompetitionsPageProps> = ({ searchTerm, onSearch }) => {
  const { stateCode } = useParams();
  const [selectedState, setSelectedState] = useUrlStateFilter('/riftbound-competitions', stateCode);
  const seo = buildCompetitionMeta('riftbound', selectedState);

  const endpoint = selectedState === 'ALL'
    ? API_ENDPOINTS.RIFTBOUND_COMPETITIONS
    : `${API_ENDPOINTS.RIFTBOUND_COMPETITIONS}?state=${selectedState}`;

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <RouteIntro
          title={seo.heading}
          description={seo.description}
          extra={hasSupporterIntro(seo.path) ? <CardReviveIntroNote /> : undefined}
        />
        <FilterRow>
          <StateFilter
            selectedState={selectedState}
            onChange={setSelectedState}
          />
          <SearchBar
            onSearch={onSearch}
            inlineControls={<CalendarTools source="riftbound" selectedState={selectedState} />}
          />
        </FilterRow>
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={endpoint} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

const LorcanaCompetitionsPage: React.FC<CompetitionsPageProps> = ({ searchTerm, onSearch }) => {
  const { stateCode } = useParams();
  const [selectedState, setSelectedState] = useUrlStateFilter('/lorcana-competitions', stateCode);
  const seo = buildCompetitionMeta('lorcana', selectedState);

  const endpoint = selectedState === 'ALL'
    ? API_ENDPOINTS.LORCANA_COMPETITIONS
    : `${API_ENDPOINTS.LORCANA_COMPETITIONS}?state=${selectedState}`;

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <RouteIntro
          title={seo.heading}
          description={seo.description}
          extra={hasSupporterIntro(seo.path) ? <CardReviveIntroNote /> : undefined}
        />
        <FilterRow>
          <StateFilter
            selectedState={selectedState}
            onChange={setSelectedState}
          />
          <SearchBar
            onSearch={onSearch}
            inlineControls={<CalendarTools source="lorcana" selectedState={selectedState} />}
          />
        </FilterRow>
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={endpoint} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

const FleshAndBloodCompetitionsPage: React.FC<CompetitionsPageProps> = ({ searchTerm, onSearch }) => {
  const { stateCode } = useParams();
  const [selectedState, setSelectedState] = useUrlStateFilter('/fleshandblood-competitions', stateCode);
  const seo = buildCompetitionMeta('fleshandblood', selectedState);

  const endpoint = selectedState === 'ALL'
    ? API_ENDPOINTS.FLESHANDBLOOD_COMPETITIONS
    : `${API_ENDPOINTS.FLESHANDBLOOD_COMPETITIONS}?state=${selectedState}`;

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <RouteIntro
          title={seo.heading}
          description={seo.description}
          extra={hasSupporterIntro(seo.path) ? <CardReviveIntroNote /> : undefined}
        />
        <FilterRow>
          <StateFilter
            selectedState={selectedState}
            onChange={setSelectedState}
          />
          <SearchBar
            onSearch={onSearch}
            inlineControls={<CalendarTools source="fleshandblood" selectedState={selectedState} />}
          />
        </FilterRow>
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={endpoint} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

// Event JSON-LD belongs on the unique /events/<slug> leaf page, not this
// multi-event calendar. HomePromoBanner remains the visible promotion source.
const HomePage: React.FC<{ searchTerm: string; onSearch: (term: string) => void }> = ({ searchTerm, onSearch }) => {
  const seo = buildHomeMeta();

  return (
    <ErrorBoundary>
      <>
        <SEO {...seo} />
        <HomePageIntro
          title={seo.heading}
          description={seo.description}
        />
        <SearchBar onSearch={onSearch} inlineControls={<CalendarTools source="shows" />} />
        <MainContent>
          <CalendarSection>
            <DeferredCalendar searchTerm={searchTerm} endpoint={API_ENDPOINTS.CALENDAR_EVENTS} />
          </CalendarSection>
        </MainContent>
      </>
    </ErrorBoundary>
  );
};

function App() {
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('q')?.toLowerCase() || '';
  });
  const [updatePrompt, setUpdatePrompt] = useState<UpdatePrompt | null>(null);
  const [showPromotedEventModal, setShowPromotedEventModal] = useState(false);
  const [showPromotionRequest, setShowPromotionRequest] = useState(false);
  const [showSponsorPopup, setShowSponsorPopup] = useState(false);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleUpdate = useCallback(() => {
    // The reloaded page runs the new bundle, whose embedded identity then
    // matches the server manifest, so the prompt cycle ends by itself.
    performHardReload();
    setUpdatePrompt(null);
  }, []);

  const handleDismissUpdate = useCallback(() => {
    // Hide the banner for now only. Detection compares the server manifest
    // against the running bundle, so the next periodic check resurfaces the
    // same pending update until it is actually applied.
    setUpdatePrompt(null);
  }, []);

  // Set up version checking on mount
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const cleanup = startVersionChecking((prompt) => {
        setUpdatePrompt(prompt);
      });

      return cleanup;
    }
  }, []);

  return (
    <ThemeProvider>
      <NotificationProvider>
        <AuthProvider>
          <ErrorBoundary>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <SavedFilterSync />
              <AnalyticsRuntime />
              <GoogleAnalyticsRuntime />
              <AnalyticsConsentBanner />
              <SponsorPopupScheduler onReady={() => setShowSponsorPopup(true)} />
              <AppContainer>
                <SkipLink href="#main-content">Skip to content</SkipLink>
                <Navbar
                  lightLogo={appLogoLight}
                  darkLogo={appLogoDark}
                />
                <BodyArea>
                  <FullWidthBanners>
                    <Suspense fallback={null}>
                      <HomePromoBanner
                        fallbackMessage="Collector's Paradise Gold Coast - VIP 11am-4pm, General Admission 12pm-4pm, 7 June 2026"
                        fallbackImageSrc={collectorsParadiseGoldCoastImage}
                        fallbackEvent={ACTIVE_PROMOTED_EVENT}
                        fallbackCtaText="See Details"
                        onFallbackCtaClick={() => setShowPromotedEventModal(true)}
                        fallbackGradient="linear-gradient(135deg, #1f2933, #0b7f8c 56%, #b7791f)"
                        fallbackEndDate={ACTIVE_PROMOTED_EVENT_BANNER_EXPIRES_AT}
                      />
                    </Suspense>
                    <EmailVerificationBanner />
                  </FullWidthBanners>
                  <BodyLeft>
                    <main id="main-content" tabIndex={-1}>
                      <Routes>
                        <Route path="/" element={<HomePage searchTerm={searchTerm} onSearch={handleSearch} />} />
                        <Route
                          path="/pokemon-competitions"
                          element={<CompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/pokemon-competitions/:stateCode"
                          element={<CompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/onepiece-competitions"
                          element={<OnePieceCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/onepiece-competitions/:stateCode"
                          element={<OnePieceCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/magic-competitions"
                          element={<MagicCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/magic-competitions/:stateCode"
                          element={<MagicCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/riftbound-competitions"
                          element={<RiftboundCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/riftbound-competitions/:stateCode"
                          element={<RiftboundCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/lorcana-competitions"
                          element={<LorcanaCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/lorcana-competitions/:stateCode"
                          element={<LorcanaCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/fleshandblood-competitions"
                          element={<FleshAndBloodCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route
                          path="/fleshandblood-competitions/:stateCode"
                          element={<FleshAndBloodCompetitionsPage searchTerm={searchTerm} onSearch={handleSearch} />}
                        />
                        <Route path="/sets" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading set releases...</div>}><SetsPage /></RouteErrorBoundary>} />
                        <Route path="/sets/:slug" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading set release...</div>}><SetDetail /></RouteErrorBoundary>} />
                        <Route path="/events/:slug" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><EventDetail /></RouteErrorBoundary>} />
                        <Route path="/saved-events" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><SavedEvents /></RouteErrorBoundary>} />
                        <Route path="/submit-event" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><SubmitEvent /></RouteErrorBoundary>} />
                        <Route path="/my-events" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><MyEvents /></RouteErrorBoundary>} />
                        <Route path="/admin/submissions" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><AdminSubmissions /></RouteErrorBoundary>} />
                        <Route path="/settings" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><Settings /></RouteErrorBoundary>} />
                        <Route path="/verify/:token" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><EmailVerification /></RouteErrorBoundary>} />
                        <Route path="/reset-password/:token" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><ResetPassword /></RouteErrorBoundary>} />
                        <Route path="/release-notes" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><ReleaseNotes /></RouteErrorBoundary>} />
                        <Route path="/privacy" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><Privacy /></RouteErrorBoundary>} />
                        <Route path="/contact" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><Contact /></RouteErrorBoundary>} />
                        <Route path="/partners" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><Partners /></RouteErrorBoundary>} />
                        <Route path="*" element={<RouteErrorBoundary fallback={<div className="text-center p-4">Loading...</div>}><NotFound /></RouteErrorBoundary>} />
                      </Routes>
                    </main>
                  </BodyLeft>
                </BodyArea>
                <Footer
                  links={[
                    {
                      src: cardReviveWordmark,
                      markSrc: cardReviveMark,
                      width: 700,
                      height: 360,
                      markWidth: 525,
                      markHeight: 525,
                      alt: 'CardRevive',
                      url: 'https://www.cardrevive.com.au/?utm_source=tcgcalendar&utm_medium=referral&utm_campaign=footer_logo',
                      sponsored: true,
                    },
                    {
                      src: cardhubAustralia,
                      alt: 'The Cardhub Australia',
                      url: 'https://linktr.ee/thecardhubaustralia',
                      width: 480,
                      height: 262,
                    },
                  ]}
                  onPromotionClick={() => setShowPromotionRequest(true)}
                />
                <MobileAppNavigation />
                {showPromotedEventModal && (
                  <Suspense fallback={null}>
                    <EventModal
                      show={showPromotedEventModal}
                      onHide={() => setShowPromotedEventModal(false)}
                      event={ACTIVE_PROMOTED_EVENT}
                    />
                  </Suspense>
                )}
                {showPromotionRequest && (
                  <Suspense fallback={null}>
                    <PromotionRequestModal
                      show={showPromotionRequest}
                      onHide={() => setShowPromotionRequest(false)}
                    />
                  </Suspense>
                )}
                {updatePrompt && (
                  <Suspense fallback={null}>
                    <UpdateNotification
                      onUpdate={handleUpdate}
                      onDismiss={updatePrompt.forced ? undefined : handleDismissUpdate}
                    />
                  </Suspense>
                )}
                {showSponsorPopup && (
                  <Suspense fallback={null}>
                    <PromoPopup onClose={() => setShowSponsorPopup(false)} />
                  </Suspense>
                )}
              </AppContainer>
            </Router>
      </ErrorBoundary>
    </AuthProvider>
  </NotificationProvider>
</ThemeProvider>
  );
}

export default App;
